import { take } from 'rxjs/operators';
import { DestroyRef, Injectable } from '@angular/core';
import { LineString, Point, Polygon } from 'geojson';
import * as L from 'leaflet';
import * as Turf from '@turf/turf';
import { GeoCodingService } from './geocoding.service';
import * as circleToPolygon from 'circle-to-polygon';
import { API, LineStringDTO, PointDTO, PolygonDTO } from '../api/api';
import { NominatimResult } from './model/nominatim';
import { GeoAssetsService } from './geo-assets.service';
import { ErrorService } from '../error.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const JSONbig = require('json-bigint');

@Injectable({
  providedIn: 'root',
})
export class GeoUtilService {
  public earthCircumference: number = 40075016.686;

  cachedPolygons: PolygonDTO[];

  constructor(
    private api: API,
    public geocoder: GeoCodingService,
    public assets: GeoAssetsService,
    private errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {
    this.cachedPolygons = [];
  }

  getCachedPolygons(ids: any[]): Promise<PolygonDTO[]> {
    return new Promise(res => {
      this.updatePolygons().then(() => {
        const resultPolygons: PolygonDTO[] = [];
        for (const poly of this.cachedPolygons) {
          for (const polygonId of ids) {
            if (this.compareBigNumberIds(poly.id, polygonId)) {
              resultPolygons.push(poly);
            }
          }
        }
        res(resultPolygons);
      });
    });
  }

  updatePolygons() {
    return new Promise(res => {
      const notCachedPolygonIds: number[] = [];
      for (const poly of this.cachedPolygons) {
        if (!poly.data) {
          notCachedPolygonIds.push(poly.id);
        }
      }
      if (notCachedPolygonIds.length > 0) {
        this.api
          .dm_geo_Polygon_retrievePolygonsByIds({ id: notCachedPolygonIds })
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (filledPolygonArray: PolygonDTO[]) => {
              for (const poly of this.cachedPolygons) {
                for (const filledPoly of filledPolygonArray) {
                  if (this.compareBigNumberIds(poly.id, filledPoly.id)) {
                    poly.data = filledPoly.data;
                  }
                }
              }
              res(true);
            },
          });
      } else {
        res(true);
      }
    });
  }

  compareBigNumberIds(idOne: any, idTwo: any): boolean {
    return JSONbig.stringify(idOne) === JSONbig.stringify(idTwo);
  }

  checkInCachedPolygon(poly: PolygonDTO) {
    let inArray: boolean = false;
    for (const element of this.cachedPolygons) {
      if (this.compareBigNumberIds(element.id, poly.id)) {
        inArray = true;
        return;
      }
    }
    if (!inArray) {
      this.cachedPolygons.push(poly);
    }
  }

  public pointZero(): Point {
    return this.coordinatesToPoint({ x: 0, y: 0 });
  }

  public nominatimBoundingBoxToLatLngBounds(boundingbox: string[]): L.LatLngBoundsExpression {
    if (boundingbox && boundingbox.length >= 3) {
      try {
        return L.latLngBounds(L.latLng(+boundingbox[0], +boundingbox[2]), L.latLng(+boundingbox[1], +boundingbox[3]));
      } catch (err) {
        return null;
      }
    } else {
      return null;
    }
  }

  public whatIsHereDiv(result: NominatimResult | NominatimResult[], position: { x: number; y: number }) {
    let res: NominatimResult;
    res = result ? (result instanceof Array ? (result.length > 0 ? result[0] : null) : result) : null;
    const divstart = '<div>';
    const divend = '</div>';
    const bstart = '<b>';
    const bend = '</b>';
    let html = '';
    html += divstart;
    html += bstart;
    html += position.y + ', ' + position.x;
    html += bend;
    html += divend;
    if (res && res.address) {
      if (res.address.road) {
        html += divstart;
        html += res.address.road + (res.address.house_number ? ' ' + res.address.house_number : '');
        html += divend;
      }
      if (res.address.village) {
        html += divstart;
        html += res.address.village;
        html += divend;
      }
      if (res.address.town || res.address.postcode) {
        html += divstart;
        let str: string = '';
        str += res.address.postcode ? res.address.postcode : '';
        str += res.address.town ? ' ' + res.address.town : '';
        str = str.trim();
        html += str;
        html += divend;
      }
      if (res.address.county) {
        html += divstart;
        html += res.address.county;
        html += divend;
      }
      if (res.address.state_district) {
        html += divstart;
        html += res.address.state_district;
        html += divend;
      }
      if (res.address.state) {
        html += divstart;
        html += res.address.state;
        html += divend;
      }
      if (res.address.country) {
        html += divstart;
        html += res.address.country;
        html += divend;
      }
    }

    return html;
  }

  public normalizeGeoJSON(geojson: any): any {
    let gjson = geojson;
    try {
      while (gjson.type === 'FeatureCollection') {
        gjson = gjson.features[0];
      }
    } catch (err) {
      this.errorHandler.errorHandler(err);
    }

    return gjson;
  }

  public polygonDTOArrayToPolygonArray(dtoarr: PolygonDTO[]): Polygon[] {
    const ret = [];
    if (dtoarr) {
      for (const dto of dtoarr) {
        if (dto.data && dto.data.coordinates.length > 0) {
          ret.push(dto.data);
        } else if (dto.simple && dto.simple.coordinates.length > 0) {
          ret.push(dto.simple);
        } else if (dto.mbr && dto.mbr.coordinates.length > 0) {
          ret.push(dto.mbr);
        }
      }
    }
    return ret;
  }

  public lineStringDTOArrayToLineStringArray(dtoarr: LineStringDTO[]): LineString[] {
    const ret = [];
    if (dtoarr) {
      for (const dto of dtoarr) {
        if (dto.data && dto.data.coordinates.length > 0) {
          ret.push(dto.data);
        } else if (dto.simple && dto.simple.coordinates.length > 0) {
          ret.push(dto.simple);
        } else if (dto.mbr && dto.mbr.coordinates.length > 0) {
          ret.push(dto.mbr);
        }
      }
    }
    return ret;
  }

  public pointDTOArrayToPointArray(dtoarr: PointDTO[]): Point[] {
    const ret = [];
    if (dtoarr) {
      for (const dto of dtoarr) {
        if (dto.data && dto.data.coordinates.length > 0) {
          ret.push(dto.data);
        }
      }
    }
    return ret;
  }

  public polygonArrayToPolygonDTOArray(arr: Polygon[]): PolygonDTO[] {
    const ret = [];
    if (arr) {
      for (const item of arr) {
        const poly: PolygonDTO = this.api.createDTO('PolygonDTO');
        poly.data = item;
        ret.push(poly);
      }
    }
    return ret;
  }

  public lineStringArrayToLineStringDTOArray(arr: LineString[]): LineStringDTO[] {
    const ret = [];
    if (arr) {
      for (const item of arr) {
        const lineStr: LineStringDTO = this.api.createDTO('LineStringDTO');
        lineStr.data = item;
        ret.push(lineStr);
      }
    }
    return ret;
  }

  public pointArrayToPointDTOArray(dtoarr: Point[]): PointDTO[] {
    const ret = [];
    if (dtoarr) {
      for (const item of dtoarr) {
        const point: PointDTO = this.api.createDTO('PointDTO');
        point.data = item;
        ret.push(point);
      }
    }
    return ret;
  }

  public removeFromArrayByGeoJson(arr: any[], anyElement: any) {
    let index = -1;

    if (arr && anyElement) {
      for (const ele of arr) {
        let elegeojson = ele.toGeoJSON();
        let anygeojson = anyElement.toGeoJSON();

        if (elegeojson.type === 'FeatureCollection') {
          elegeojson = elegeojson.features[0];
        }

        if (anygeojson.type === 'FeatureCollection') {
          anygeojson = anygeojson.features[0];
        }

        try {
          if (Turf.booleanEqual(elegeojson, anygeojson)) {
            index = arr.indexOf(ele);
            break;
          }
        } catch (e) {}
      }
    }

    if (index !== -1) {
      arr.splice(index, 1);
    }

    return arr;
  }

  public circleRadiusByZoom(zoom: number = 1, radiusPx: number = 32, center: L.LatLng): number {
    return ((this.earthCircumference * Math.abs(Math.cos((center.lat * Math.PI) / 180))) / Math.pow(2, zoom + 8)) * radiusPx;
  }

  public circle(point: Point, radius: number, numberOfEdges: number = 16): Polygon {
    const polygon = circleToPolygon(point.coordinates, radius, numberOfEdges);
    return polygon;
  }

  public coordinatesToPoint(coordinates: { x: number; y: number; z?: number }): Point {
    return this.createPoint(coordinates.x, coordinates.y, coordinates.z);
  }

  public pointToCoordinates(point: Point): { x: number; y: number; z?: number } {
    if (point) {
      const ret: { x: number; y: number; z?: number } = {
        x: point.coordinates[0],
        y: point.coordinates[1],
      };
      if (point.coordinates[2]) {
        ret.z = point.coordinates[2];
      }

      return ret;
    }
    return null;
  }

  public createPoint(x?: number, y?: number, z?: number): Point {
    const p: Point = {
      type: 'Point',
      coordinates: [x ? x : 0.0, y ? y : 0.0],
    };

    if (z) {
      p.coordinates.push(z);
    }

    return p;
  }

  public pointToLatLng(point: Point): L.LatLng {
    return new L.LatLng(point.coordinates[1], point.coordinates[0], point.coordinates[2]);
  }

  public coordinatesToLatLng(coordinates: { x: number; y: number; z?: number }): L.LatLng {
    return new L.LatLng(coordinates.y, coordinates.x, coordinates.z);
  }
}
